import React from 'react';
import { useSignal } from '@preact/signals-react';
import { FaPlus, FaCopy, FaTimes, FaSave } from 'react-icons/fa';
import { FaRegPaste } from "react-icons/fa6";
import { HiOutlineTrash } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import ParsePayloadModal from './ParsePayloadModal';
import { FieldItem } from './FieldsModal';
import NewTooltip from '../../atoms/NewTooltip';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = `${process.env.REACT_APP_DATA_PROVIDER_API_URL}/report-types`;

interface ReportType {
    id: string;
    name: string;
    provider: string;
    documentType: string;
    description: string;
    params: Record<string, string>;
    fields: any[];
    status: boolean;
}

interface FieldMappingModalProps {
    show: boolean;
    reportType: ReportType | null;
    onClose: () => void;
    onSave: () => void;
}

const FieldMappingModal: React.FC<FieldMappingModalProps> = ({
    show,
    reportType,
    onClose,
    onSave
}) => {
    const fields = useSignal<FieldItem[]>([]);
    const showParser = useSignal(false);
    const payloadText = useSignal('');
    const saving = useSignal(false);

    React.useEffect(() => {
        if (reportType && reportType.fields) {
            fields.value = reportType.fields.map(field => ({
                header: field.header || '',
                field: field.field || field.payloadKey || ''
            }));
        }
    }, [reportType]);

    const addField = () => {
        fields.value = [...fields.value, { header: '', field: '' }];
    };

    const updateField = (index: number, field: Partial<FieldItem>) => {
        fields.value = fields.value.map((f, i) => (i === index ? { ...f, ...field } : f));
    };

    const removeField = (index: number) => {
        fields.value = fields.value.filter((_, i) => i !== index);
    };

    const parsePayload = () => {
        try {
            const json = JSON.parse(payloadText.value);
            const result: FieldItem[] = [];

            const walk = (obj: any, prefix = ''): void => {
                if (Array.isArray(obj)) {
                    obj.forEach((item, idx) => walk(item, `${prefix}[${idx}]`));
                    return;
                }

                if (obj && typeof obj === 'object') {
                    Object.entries(obj).forEach(([k, v]) => {
                        const path = prefix ? `${prefix}.${k}` : k;
                        if (v && typeof v === 'object') {
                            walk(v, path);
                        } else {
                            result.push({ header: k, field: path });
                        }
                    });
                }
            };

            walk(json);
            fields.value = result;
            showParser.value = false;
            payloadText.value = '';
        } catch (err) {
            toast.error('Payload inválido');
        }
    };

    const saveFields = async () => {
        if (!reportType) return;

        saving.value = true;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${reportType.id}/fields`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    fields: fields.value
                })
            });

            if (response.ok) {
                toast.success('Mapeamento de campos salvo com sucesso!');
                onSave();
                onClose();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar mapeamento de campos');
            }
        } catch (error) {
            toast.error('Erro ao salvar mapeamento de campos');
        } finally {
            saving.value = false;
        }
    };

    const copyJson = () => {
        const data = JSON.stringify(fields.value, null, 2);
        navigator.clipboard.writeText(data);
        toast.success('JSON copiado para a área de transferência');
    };

    if (!show) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Mapeamento de Campos - {reportType?.name}
                        </h2>
                        <div className="flex space-x-2">
                            <NewTooltip content="Parser Payload" alignment="bottom">
                                <button
                                    className="bg-blue-500 text-white p-2 rounded hover:bg-blue-700"
                                    onClick={() => showParser.value = true}
                                >
                                    <FaRegPaste />
                                </button>
                            </NewTooltip>
                            <NewTooltip content="Adicionar Campo" alignment="bottom">
                                <button
                                    className="bg-green-600 text-white p-2 rounded hover:bg-green-800"
                                    onClick={addField}
                                >
                                    <FaPlus />
                                </button>
                            </NewTooltip>
                            <NewTooltip content="Copiar JSON" alignment="bottom">
                                <button
                                    className="bg-blue-500 text-white p-2 rounded hover:bg-blue-700"
                                    onClick={copyJson}
                                >
                                    <FaCopy />
                                </button>
                            </NewTooltip>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                        <table className="w-full table-auto border border-gray-200 dark:border-slate-600">
                            <thead>
                                <tr className="bg-slate-100 dark:bg-slate-700">
                                    <th className="px-2 py-1 border-b border-gray-200 dark:border-slate-600 text-left text-gray-900 dark:text-white">Header</th>
                                    <th className="px-2 py-1 border-b border-gray-200 dark:border-slate-600 text-left text-gray-900 dark:text-white">Key</th>
                                    <th className="px-2 py-1 border-b border-gray-200 dark:border-slate-600 w-12"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.value.map((field, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700'}>
                                        <td className="px-2 py-1 border-b border-gray-200 dark:border-slate-600">
                                            <input
                                                className="w-full border border-gray-300 dark:border-slate-600 rounded p-1 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                                value={field.header}
                                                placeholder="Header"
                                                onChange={(e) => updateField(index, { header: e.target.value })}
                                            />
                                        </td>
                                        <td className="px-2 py-1 border-b border-gray-200 dark:border-slate-600">
                                            <input
                                                className="w-full border border-gray-300 dark:border-slate-600 rounded p-1 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                                                value={field.field}
                                                placeholder="Payload key"
                                                onChange={(e) => updateField(index, { field: e.target.value })}
                                            />
                                        </td>
                                        <td className="px-2 py-1 border-b border-gray-200 dark:border-slate-600 text-center">
                                            <NewTooltip content="Remover" alignment="bottom">
                                                <button
                                                    className="text-red-600 hover:text-red-800"
                                                    onClick={() => removeField(index)}
                                                >
                                                    <HiOutlineTrash />
                                                </button>
                                            </NewTooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 dark:border-slate-700">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600 rounded-lg transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={saveFields}
                            disabled={saving.value}
                            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
                        >
                            <FaSave className="mr-2" size={14} />
                            {saving.value ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>

            <ParsePayloadModal
                id="parser-modal"
                show={showParser.value}
                payload={payloadText.value}
                onChange={(v) => payloadText.value = v}
                onParse={parsePayload}
                onClose={() => showParser.value = false}
            />
        </>
    );
};

export default FieldMappingModal;
